<template>
    <input
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :min="min"
        :max="max"
        :step="step"
        :readonly="readonly"
        :required="required"
        class="custom-input"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        v-bind="$attrs"
    />
</template>

<script setup>
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    type: {
        type: String,
        default: 'text'
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    readonly: {
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false
    },
    min: {
        type: [String, Number],
        default: undefined
    },
    max: {
        type: [String, Number],
        default: undefined
    },
    step: {
        type: [String, Number],
        default: undefined
    }
})

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'input'])

const handleInput = (event) => {
    let value = event.target.value
    
    // Handle number inputs
    if (props.type === 'number') {
        // Allow empty string for clearing
        if (value === '') {
            emit('update:modelValue', '')
            emit('input', event)
            return
        }
        
        // Convert to number
        const numValue = parseFloat(value)
        if (!isNaN(numValue)) {
            emit('update:modelValue', numValue)
        }
    } else {
        emit('update:modelValue', value)
    }
    
    emit('input', event)
}

const handleFocus = (event) => {
    emit('focus', event)
}

const handleBlur = (event) => {
    emit('blur', event)
}
</script>

<style lang="scss" scoped>
.custom-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--theme-control, #4E4E4E);
    border-radius: 6px;
    background: var(--theme-inset, #2F2F2F);
    color: var(--theme-text, #ffffff);
    font-size: 0.9rem;
    font-family: inherit;
    font-weight: 500;
    transition: none;
    box-sizing: border-box;
    min-height: 38px;
    
    &::placeholder {
        color: var(--theme-muted, #B4B4B4);
    }
    
    &:hover:not(:focus):not(:disabled) {
        border-color: var(--theme-accent-text, #6a6a6a);
    }
    
    &:focus {
        outline: none;
        border-color: var(--theme-accent-text, #ffffff);
        background-color: var(--theme-inset, #2F2F2F);
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background-color: var(--theme-inset, #1a1a1a);
        color: var(--theme-muted, #666666);
    }
    
    &:readonly {
        background-color: var(--theme-inset, #1a1a1a);
        cursor: default;
    }
}
</style>
